import { CSSProperties, ReactNode } from 'react';
import { cn } from 'shared/utils/cn';

// Skeletons espelham as medidas reais dos widgets (mesmos paddings, raios, grids e alturas
// de linha), para a troca skeleton → conteúdo não "pular" a tela.

export function Skeleton({ className, style }: { className?: string; style?: CSSProperties }) {
  return <div aria-hidden="true" className={cn('skeleton', className)} style={style} />;
}

/** Área em carregamento: anuncia o estado para leitores de tela e esconde os "ossos". */
export function SkeletonRegion({ label = 'Carregando...', children }: { label?: string; children: ReactNode }) {
  return (
    <div role="status" aria-live="polite" aria-busy="true">
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

/** Mesma caixa do <Card> (sem hover), para skeletons de cards. */
export function SkeletonCard({ className, children }: { className?: string; children?: ReactNode }) {
  return (
    <div aria-hidden="true" className={cn('rounded-2xl border border-border/40 bg-surface p-6 shadow-card', className)}>
      {children}
    </div>
  );
}

/** Linhas de texto; a última fica mais curta, como um parágrafo de verdade. */
export function SkeletonText({ lines = 2, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton key={index} className={cn('h-3', index === lines - 1 && lines > 1 ? 'w-3/5' : 'w-full')} />
      ))}
    </div>
  );
}

export function SkeletonAvatar({ className }: { className?: string }) {
  return <Skeleton className={cn('h-8 w-8 flex-shrink-0 rounded-full', className)} />;
}

export function SkeletonBadge({ className }: { className?: string }) {
  return <Skeleton className={cn('h-6 w-20 rounded-full', className)} />;
}

export function SkeletonButton({ className }: { className?: string }) {
  return <Skeleton className={cn('h-10 rounded-lg', className)} />;
}

/** Espelha <PillTabs>: pílulas de 36px com larguras variadas como os rótulos reais. */
export function SkeletonPills({ widths = [72, 88, 120, 96], className }: { widths?: number[]; className?: string }) {
  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {widths.map((width, index) => (
        <Skeleton key={index} className="h-9 rounded-full" style={{ width }} />
      ))}
    </div>
  );
}

/** Espelha <StatTile>: rótulo curto em caps + valor. */
export function SkeletonStatTile() {
  return (
    <div aria-hidden="true" className="rounded-2xl border border-border/40 bg-surface p-4 shadow-card">
      <Skeleton className="h-3 w-24" />
      <Skeleton className="mt-3 h-6 w-16" />
    </div>
  );
}

export function SkeletonStatGrid({
  count = 4,
  className = 'mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4',
}: {
  count?: number;
  className?: string;
}) {
  return (
    <div className={className}>
      {Array.from({ length: count }, (_, index) => (
        <SkeletonStatTile key={index} />
      ))}
    </div>
  );
}

/** Espelha <MetricTile>: rótulo + ícone, valor grande, selo de variação + sparkline, legenda. */
export function SkeletonMetricTile() {
  return (
    <div aria-hidden="true" className="rounded-2xl border border-border/40 bg-surface p-4 shadow-card">
      <div className="flex items-start justify-between gap-2">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-8 w-8 rounded-xl" />
      </div>
      <Skeleton className="mt-2 h-7 w-28" />
      <div className="mt-2 flex items-center justify-between">
        <Skeleton className="h-5 w-14 rounded-full" />
        <Skeleton className="h-5 w-16" />
      </div>
      <Skeleton className="mt-2 h-2.5 w-20" />
    </div>
  );
}

/** Espelha as linhas clicáveis das listas (reservas, turmas, assinaturas, torneios). */
export function SkeletonRowCard({ lines = 2 }: { lines?: number }) {
  return (
    <div aria-hidden="true" className="flex flex-col gap-2 rounded-xl border border-border/40 bg-surface p-4">
      <div className="flex items-center justify-between gap-2">
        <Skeleton className="h-4 w-32" />
        <SkeletonBadge className="w-24" />
      </div>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton key={index} className={cn('h-3', index === 0 ? 'w-3/5' : index === 1 ? 'w-2/5' : 'w-1/3')} />
      ))}
      <div className="mt-1 flex items-center justify-between">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-3 w-16" />
      </div>
    </div>
  );
}

/** Espelha os painéis de detalhe à direita: cabeçalho, pares chave-valor, lista e ações. */
export function SkeletonDetailPanel({ rows = 4 }: { rows?: number }) {
  return (
    <SkeletonCard>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="mt-2 h-5 w-48" />
          <Skeleton className="mt-2 h-3 w-40" />
        </div>
        <SkeletonBadge className="w-28" />
      </div>
      <div className="mt-5 grid grid-cols-2 gap-4">
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index}>
            <Skeleton className="h-2.5 w-20" />
            <Skeleton className="mt-2 h-4 w-24" />
          </div>
        ))}
      </div>
      <Skeleton className="mt-6 h-3 w-32" />
      <div className="mt-3 flex flex-col gap-3">
        {Array.from({ length: rows }, (_, index) => (
          <div key={index} className="flex items-center gap-3">
            <SkeletonAvatar />
            <Skeleton className="h-3 w-28" />
            <Skeleton className="ml-auto h-3 w-24" />
          </div>
        ))}
      </div>
      <div className="mt-6 grid grid-cols-2 gap-3">
        <SkeletonButton />
        <SkeletonButton />
      </div>
    </SkeletonCard>
  );
}

/** Lista à esquerda + painel de detalhe à direita, o layout das telas operacionais. */
export function SkeletonListDetail({
  rows = 4,
  lines = 2,
  columns = 'lg:grid-cols-[1.1fr_1fr]',
  className = 'mt-4',
}: {
  rows?: number;
  /** Linhas de texto em cada item da lista (turmas e torneios têm 3). */
  lines?: number;
  columns?: string;
  className?: string;
}) {
  return (
    <div className={cn('grid gap-6', columns, className)}>
      <div className="flex flex-col gap-3">
        {Array.from({ length: rows }, (_, index) => (
          <SkeletonRowCard key={index} lines={lines} />
        ))}
      </div>
      <SkeletonDetailPanel />
    </div>
  );
}

type ChartKind = 'line' | 'bars' | 'columns' | 'mix';

const COLUMN_HEIGHTS = [55, 70, 62, 85, 78, 92];
const BAR_WIDTHS = [100, 82, 64, 48];

/** Espelha <ChartCard>: título, subtítulo, botões CSV/tabela e a área do gráfico. */
export function SkeletonChartCard({ kind = 'bars', className }: { kind?: ChartKind; className?: string }) {
  return (
    <SkeletonCard className={cn('h-full', className)}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <Skeleton className="h-4 w-44 max-w-full" />
          <Skeleton className="mt-2 h-3 w-56 max-w-full" />
        </div>
        <div className="flex flex-shrink-0 gap-2">
          <Skeleton className="h-7 w-12 rounded-lg" />
          <Skeleton className="h-7 w-20 rounded-lg" />
        </div>
      </div>

      <div className="mt-6">
        {kind === 'line' && (
          <div className="relative h-56">
            {[0, 1, 2, 3].map((line) => (
              <div key={line} className="absolute inset-x-0 border-t border-dashed border-border/60" style={{ top: `${line * 30}%` }} />
            ))}
            <Skeleton className="absolute inset-x-0 bottom-6 h-2/3 rounded-xl opacity-70" />
            <div className="absolute inset-x-6 bottom-0 flex justify-between">
              {Array.from({ length: 6 }, (_, index) => (
                <Skeleton key={index} className="h-3 w-8" />
              ))}
            </div>
          </div>
        )}

        {kind === 'columns' && (
          <div className="flex h-44 items-end gap-3">
            {COLUMN_HEIGHTS.map((height, index) => (
              <div key={index} className="flex flex-1 flex-col items-center gap-2">
                <Skeleton className="w-full rounded-t-md rounded-b-none" style={{ height: `${height}%` }} />
                <Skeleton className="h-3 w-8" />
              </div>
            ))}
          </div>
        )}

        {kind === 'bars' && (
          <div className="flex flex-col gap-4">
            {BAR_WIDTHS.map((width, index) => (
              <div key={index}>
                <div className="mb-1.5 flex justify-between">
                  <Skeleton className="h-3 w-28" />
                  <Skeleton className="h-3 w-16" />
                </div>
                <Skeleton className="h-3 rounded-full" style={{ width: `${width}%` }} />
              </div>
            ))}
          </div>
        )}

        {kind === 'mix' && (
          <div>
            <Skeleton className="h-8 w-full rounded-lg" />
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-3">
              {Array.from({ length: 4 }, (_, index) => (
                <Skeleton key={index} className="h-3 w-32" />
              ))}
            </div>
          </div>
        )}
      </div>
    </SkeletonCard>
  );
}

/** Espelha os posts do mural: autor com avatar, selo, texto e rodapé de ações. */
export function SkeletonPostCard({ withAction = true }: { withAction?: boolean }) {
  return (
    <SkeletonCard>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <SkeletonAvatar className="h-9 w-9" />
          <div>
            <Skeleton className="h-3.5 w-28" />
            <Skeleton className="mt-1.5 h-2.5 w-20" />
          </div>
        </div>
        <SkeletonBadge className="w-16" />
      </div>
      <SkeletonText lines={2} className="mt-4" />
      <Skeleton className="mt-3 h-3 w-40" />
      {withAction && <SkeletonButton className="mt-4 w-full" />}
    </SkeletonCard>
  );
}
